<template>
  <div class="md:px-2 px-0">
    <!-- Tabs Navigation with Arrows -->
    <div class="relative flex items-center outer-box">
      <!-- Left Arrow -->
      <button
        v-if="canScrollLeft"
        @click="scrollLeft"
        class="absolute left-0 z-10 flex items-center justify-center pr-8 arrow-btn-left"
      >
        <Icon name="arrow-left" class="text-gray" />
      </button>
      <!-- Tabs Container -->
      <div
        ref="tabsContainer"
        class="flex space-x-4 overflow-x-auto no-scrollbar transition-all duration-300 w-full"
        :class="canScrollLeft ? 'ml-8' : 'ml-0'"
        @scroll="handleScroll"
      >
        <div
          v-for="(tab, index) in tabs"
          :key="index"
          class="ui-font tab px-2 py-2 pb-3 text-sm font-medium text-center whitespace-nowrap cursor-pointer border-b-2"
          :class="[
            selectedTab.id === tab.id
              ? 'border-yka-blue'
              : 'border-lighter-gray text-gray',
          ]"
          @click="selectTab(tab)"
          :id="`${hashPrefix}${tab.id}`"
        >
          {{ tab.name }}
        </div>
      </div>
      <!-- Right Arrow -->
      <button
        v-if="canScrollRight"
        @click="scrollRight"
        class="absolute right-0 z-10 flex items-center justify-center pl-8 arrow-btn-right"
      >
        <Icon name="arrow-right" class="text-gray" />
      </button>
      <button
        v-if="showPlusBtn"
        title="Add topic"
        class="flex items-center justify-center px-2 py-2 pb-3"
        @click="actions.show_follow_modal.handler"
      >
        <Icon :name="actions?.show_follow_modal?.icon_name" class="text-gray" />
      </button>
    </div>

    <!-- Tab Content Section -->
    <div class="mx-2 mt-4">
      <template
        v-if="tabs.length && selectedTab.id && getParams.categories !== null"
      >
        <Feed
          v-if="style === 'Feed'"
          :params="getParams"
          :key="`${selectedTab.id}-feed-${tabs.length}`"
        />
        <OrbitPosts
          v-else
          :params="getParams"
          :key="`${selectedTab.id}-default-${tabs.length}`"
        >
          <template v-slot:whenempty>
            <div class="mb-8 mt-2 text-center ui-font">
              No posts found in {{ selectedTab?.name }} category!
            </div>
          </template>
        </OrbitPosts>
      </template>
    </div>
  </div>
</template>

<script>
import { useRoute, useRouter } from "vue-router";
import { ref, onMounted, nextTick, onUnmounted, watch, computed } from "vue";
import Icon from "@/components/Icons.vue";

export default {
  props: {
    tabs: {
      type: Array,
      required: true,
    },
    logged_in_id: {
      type: Number,
      required: true,
    },
    style: String,
    actions: Object,
  },
  components: { Icon },
  setup(props) {
    const selectedTab = ref({});
    const canScrollLeft = ref(false);
    const canScrollRight = ref(false);
    const tabsContainer = ref(null);
    const router = useRouter();
    const route = useRoute();
    const hashPrefix = "term_tabs_tab-";

    const categories_exclude =
      window.ykasettings && window.ykasettings.exclude_categories
        ? Object.values(window.ykasettings.exclude_categories).toString()
        : 0;

    const getParams = computed(() => ({
      categories:
        typeof selectedTab?.value?.id === "number" ? selectedTab?.value?.id : 0,
      style: props?.style,
      categories_exclude,
      pagination: 1,
      ...(props.logged_in_id &&
        selectedTab.value?.feed && { feed: selectedTab.value?.feed }),
      ...(props.logged_in_id &&
        selectedTab.value?.location && {
          location: selectedTab.value?.location,
        }),
    }));

    const showPlusBtn = computed(
      () =>
        props.actions?.show_follow_modal &&
        props.logged_in_id &&
        props.tabs.length &&
        props.tabs.length < 9,
    );

    const syncTabWithUrl = () => {
      const tabId = route.hash ? route.hash.replace(`#${hashPrefix}`, "") : "";

      const tabIdFromUrl = tabId ? tabId : 0;

      const matchedTab = props.tabs.find((tab) => tab.id == tabIdFromUrl);

      if (matchedTab) {
        selectedTab.value = matchedTab;
      } else if (props.tabs.length) {
        selectedTab.value = props.tabs[0];

        router.replace({
          hash: "",
        });
      }

      checkScrollState();
    };

    const selectTab = (item) => {
      selectedTab.value = item;

      router.push({
        hash: `#${hashPrefix}${item.id}`,
      });
    };

    const scrollLeft = () => {
      tabsContainer.value.scrollBy({
        left: -250,
        behavior: "smooth",
      });
    };

    const scrollRight = () => {
      tabsContainer.value.scrollBy({
        left: 250,
        behavior: "smooth",
      });
    };

    const handleScroll = () => {
      checkScrollState();
    };

    const checkScrollState = () => {
      nextTick(() => {
        const { scrollWidth, clientWidth, scrollLeft } = tabsContainer.value;
        canScrollLeft.value = scrollLeft > 0;
        canScrollRight.value = scrollLeft + clientWidth < scrollWidth;
      });
    };

    watch(
      () => route.hash,
      () => {
        syncTabWithUrl();
      },
    );

    watch(
      () => props.tabs,
      () => {
        syncTabWithUrl();
      },
    );

    onMounted(() => {
      syncTabWithUrl();
      checkScrollState();
      window.addEventListener("resize", checkScrollState);
    });

    onUnmounted(() => {
      selectedTab.value = {};
      window.removeEventListener("resize", checkScrollState);
    });

    return {
      selectTab,
      getParams,
      scrollLeft,
      hashPrefix,
      showPlusBtn,
      scrollRight,
      selectedTab,
      handleScroll,
      canScrollLeft,
      tabsContainer,
      canScrollRight,
    };
  },
};
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.border-bottom-line {
  border-bottom-width: 2px;
  margin-top: -2px;
  border-color: rgb(232 230 230);
}

.arrow-btn-right {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.75) 25%,
    rgba(255, 255, 255, 0.9) 50%,
    rgb(255, 255, 255) 75%
  );
}

.arrow-btn-left {
  background: linear-gradient(
    -90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.75) 25%,
    rgba(255, 255, 255, 0.9) 50%,
    rgb(255, 255, 255) 75%
  );
}

.outer-box {
  box-shadow: rgb(221, 221, 221) 0px -2px 0px inset;
}
</style>
